<template>
    <page-template page-id="Credentials" ref="super">

        <document-title :title="$tb('pages.loginOTP.documentTitle')"></document-title>

        <template v-slot:before v-if="isTitlebarActive">
            <titlebar :text="$tb('pages.loginOTP.header', {0: $store.state.appName})"></titlebar>
        </template>

        <div class="ui column">

            <brand-header :text="$t('pages.loginOTP.title')"
                          :has-extension="hasHeaderExtension"
                          :has-logo="hasBrandLogo">
            </brand-header>

            <p v-html="getSubheaderText"></p>

            <messages id="Notifications" ref="postman" error-element-id="server-errors"></messages>

            <input type="hidden" id="username" name="username" v-model="username">
            <input type="hidden" id="rid" name="rid" v-model="rid">

            <validated-field id="validation-errors" :show-errors-on="showErrors" :has-any-errors="$v.otp.$invalid || hasServerSideErrors"
                             :validation-rules="[{value: $v.otp.notEmpty, msg: $t('common.validation.otp.empty')},
                                                 {value: $v.otp.hasFormat, showOn: $v.otp.notEmpty, msg: $t('common.validation.otp.format')}]">
                <tan-input-field id="tan" v-model="$v.otp.$model" ref="tanInput"
                                 :aria-label-tan-type="$t('aria.common.otp')" @focusout="otpFocusOut"
                                 :invalid="$v.otp.$invalid || hasServerSideErrors"
                                 aria-error-element-id="validation-errors server-errors">
                </tan-input-field>
            </validated-field>

            <div class="left aligned field" v-if="this.supportAcr('REMEMBER_ME')">
                <div class="ui custom-ui checkbox">
                    <input id="rememberMe" type="checkbox" v-model="rememberMe">
                    <label for="rememberMe">{{$t('common.label.rememberMe')}}</label>
                </div>
            </div>

            <div class="field">
                <button id="confirm" type="submit" class="ui fluid large primary submit button" @click.prevent="onConfirm" ref="continueBtn">
                    {{$t('common.button.continue')}}
                </button>
            </div>

            <div class="field" v-if="getConfig('loginOtp_hasBackButton')">
                <button id="back" @click="goBack" class="ui fluid basic button">
                    {{$t('common.button.back')}}
                </button>
            </div>

            <h4 class="custom-ui header" v-html="$t('pages.loginOTP.resendCode.header')"></h4>

            <p v-if="isMobileLoginDisabled" v-html="$t('pages.loginOTP.resendCode.textEmail')"></p>

            <p v-else v-html="$t('pages.loginOTP.resendCode.text')"></p>

            <div class="field">
                <resend-button id="resend" ref="resendBtn"
                               :value="$t('common.button.resend')"
                               :action="resendOtp"></resend-button>
            </div>

        </div>
    </page-template>
</template>

<script>
import PageTemplate from './template/PageTemplate.vue'
import Titlebar from '../components/Titlebar.vue'
import BrandHeader from '../components/BrandHeader.vue'
import ResendButton from '../components/ResendButton.vue'
import Messages from '../components/Messages.vue'
import TanInputField from '../components/TanInputField.vue'
import ValidatedField from '../components/ValidatedField.vue'
import { mixin } from '../js/ciam-mixin.js'
import { brand } from '../js/brand.js'
import { getRecaptchaToken } from '../js/helpers.js'

export default {
  components: {
    PageTemplate,
    Titlebar,
    BrandHeader,
    TanInputField,
    ResendButton,
    Messages,
    ValidatedField
  },
  mixins: [mixin, brand],
  data: function () {
    return {
      username: sessionStorage.getItem('storedusername') || this.$store.state.username,
      rid: sessionStorage.getItem('storedrid') || this.$store.state.rid,
      otp: null,
      rememberMe: this.$defaults.REMEMBER_ME_SELECTED,
      showErrors: false,
      hasServerSideErrors: false,
      isRecaptchaActive: this.$store.state.isRecaptchaActive,
      recaptchaSiteKey: this.$store.state.recaptchaSiteKey,
      isMobileLoginDisabled: this.$store.state.isMobileLoginDisabled
    }
  },
  validations () {
    return {
      otp: {
        notEmpty (value) {
          return !!value && value !== '------'
        },
        hasFormat (value) {
          return /[\d]{6}/.test(value) // otp needs to be 6 digits
        }
      }
    }
  },
  methods: {
    onConfirm: function (e) {
      this.$refs.postman.clear() //  remove previous errors
      this.showErrors = false
      this.hasServerSideErrors = false

      if (!this.isFormValid()) return

      this.$refs.super.startLoading()

      const payload = this.$api.payloads.buildAuthenticationPayload(this.username, this.otp, this.rememberMe, this.rid)
      this.$api.login.authenticateWithOtp(payload, this.handleRoutingResponse, this.onFail)
    },
    isFormValid: function () {
      if (!this.$v.$anyDirty || this.$v.$anyError || this.$v.$invalid) {
        const _this = this

        this.focusFirstErrorField(function () {
          _this.showErrors = true
        })

        return false
      }

      return true
    },
    onFail: function (response) {
      this.$refs.tanInput.focus()
      this.onAjaxError(response)
      this.$refs.super.stopLoading()
      this.hasServerSideErrors = true
    },
    goBack: function () {
      window.history.length > 1 ? window.history.back() : this.$router.push('/')
    },
    resendOtp: async function () {
      this.$refs.postman.clear()
      this.$refs.super.startLoading()

      const recaptchaToken = await getRecaptchaToken(this.isRecaptchaActive, this.recaptchaSiteKey, this.$recaptcha.actions.LOGIN_OTP)

      this.$api.codes.sendOTP(this.username, recaptchaToken, this.handleResendResponse, this.onFail)
    },
    handleResendResponse: function () {
      this.$refs.tanInput.reset()// reset the TAN input
      this.$refs.tanInput.focus()
      this.$refs.resendBtn.disableActionForTime(30)
      this.$refs.postman.showMessage('info', this.$t('common.msg.resendOk', {
        0: this.$tb('common.label.otp'),
        1: this.username
      }))
      this.$refs.super.stopLoading()
    },
    checkHideErrors: function () {
      if (this.$v.$invalid) return
      this.showErrors = false
    },
    otpFocusOut: function () {
      this.$refs.continueBtn.focus()
    },
    focusOtp: function () {
      this.$refs.tanInput.focus()
    }
  },
  computed: {
    getSubheaderText: function () {
      if (this.isMFA()) {
        return this.$t('pages.loginOTP.subheaderMFA', { 0: this.$store.state.appName, 1: this.username })
      } else {
        return this.$t('pages.loginOTP.subheader', { 0: '<b>' + this.username + '</b>' })
      }
    }
  },
  mounted: function () {
    console.debug(`Login with OTP [username=${this.username}]`)
    this.focusOtp()
  },
  watch: {
    $v: {
      handler () {
        this.checkHideErrors()
      },
      deep: true
    }
  }
}
</script>
